import { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import useEventLogger from './useEventLogger'
import { timeout } from '../../utilities/helpers'

const AUTO_OPEN_DELAY_MS = 700

const DETAIL_PANEL_ID_PARAM = 'id'

export const getDetailPanelPath = (pathname: string, rowId: string) =>
    `${pathname}?${DETAIL_PANEL_ID_PARAM}=${encodeURIComponent(rowId)}`

type AutoOpenLocation = ReturnType<typeof useLocation> & {
    autoOpenData?: {
        isAutoOpenPanel?: boolean
        autoOpenData?: { _id?: string }
        previousDataStack?: unknown[]
    } | null
}

// A link's ?id= survives a new tab or a refresh; an in-app navigation's autoOpenData also carries the stack to go back to.
const getRequestedRow = (location: AutoOpenLocation) => {
    const rowIdInUrl = new URLSearchParams(location.search).get(DETAIL_PANEL_ID_PARAM)
    if (rowIdInUrl) return { rowId: rowIdInUrl, previousDataStack: [], isInUrl: true }

    const { autoOpenData } = location
    if (!autoOpenData?.isAutoOpenPanel || !autoOpenData.autoOpenData?._id) return null

    return {
        rowId: autoOpenData.autoOpenData._id,
        previousDataStack: autoOpenData.previousDataStack ?? [],
        isInUrl: false,
    }
}

export const useTableDetailPanel = <TRow extends { _id: string }>(rows: TRow[], viewedType: string) => {
    const location = useLocation() as AutoOpenLocation
    const history = useHistory()
    const { logEvent, ACTIONS } = useEventLogger()

    const [openRowId, setOpenRowId] = useState<string | null>(null)
    const [navigationStack, setNavigationStack] = useState<unknown[]>([])

    const openPanel = useCallback(
        (rowId: string) => {
            logEvent(ACTIONS.VIEW, { type: viewedType, _id: rowId })
            setOpenRowId(rowId)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [viewedType]
    )

    const closePanel = useCallback(() => {
        setOpenRowId(null)
        setNavigationStack([])
        location.autoOpenData = null
    }, [location])

    useEffect(() => {
        const requestedRow = getRequestedRow(location)

        if (!requestedRow || !rows.some((row) => row._id === requestedRow.rowId)) return

        let isCancelled = false

        setNavigationStack(requestedRow.previousDataStack)
        timeout(AUTO_OPEN_DELAY_MS).then(() => {
            if (isCancelled) return

            openPanel(requestedRow.rowId)
            // Consumed once opened, or closing the panel and refreshing would open it again.
            if (requestedRow.isInUrl) history.replace(location.pathname)
        })

        return () => {
            isCancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, location.autoOpenData, rows])

    return {
        openRow: rows.find((row) => row._id === openRowId) ?? null,
        navigationStack,
        openPanel,
        closePanel,
    }
}
